import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import moment from 'moment';
import { Subscription } from 'rxjs';

import { BaseComponent } from '../base/base-component.component';
import { DatePreset } from '../models/DatePreset';
import { DatePresetService, DatePresetServiceType } from '../services/date-preset.service';
import { HelperService } from '../services/helper.service';
import { TranslateService } from '../services/translate.service';

export enum DatePanelSize {
  Small = 0,
  Medium = 1,
}
export enum DatePanelView {
  Day = 0,
  Month = 1,
  Year = 2,
}
export enum DatePanelMode {
  Regular = 0,
  DatePreset = 1,
}
export class DateIntervals {
  public id?: string;
  public label?: string;
  public startDate: string;
  public endDate: string;

  constructor(id: string = null, label: string = null, startDate: string, endDate: string) {
    this.id = id;
    this.label = label;
    this.startDate = startDate;
    this.endDate = endDate;
  }
}

class DateItem {
  public selectable: boolean;
  public selected: boolean;

  public isFixed: boolean;
  public isBetween: boolean;
  public isStartDate: boolean;
  public isEndDate: boolean;
  public isFirstDayOfWeek: boolean;
  public isLastDayOfWeek: boolean;
  public startEqualsEnd: boolean;

  public month: string;
  public isToday: boolean;
  public date: moment.Moment;
  public value: number;

  constructor(date?: moment.Moment, month?: string) {
    if (date) {
      this.date = date;
    }

    if (month) {
      this.month = month;
    }
  }
}

@Component({
  standalone: false,
  // tslint:disable-next-line: component-selector
  selector: 'daui-date-panel',
  templateUrl: './date-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./date-panel.component.less'],
})
export class DatePanelComponent extends BaseComponent implements OnChanges {
  @Input()
  dateFormat = 'YYYY-MM-DD';
  @Input() mode = DatePanelMode.Regular;
  @Input() view = DatePanelView.Day;
  @Input() size: DatePanelSize = DatePanelSize.Medium;
  @Input() intervalSelection = false;
  @Input() hasMonth = true;
  @Input() hasYear = true;
  @Input() hasPrev = true;
  @Input() hasNext = true;
  @Input() hasToday = true;
  @Input() hasDatePreset = false;
  @Input() set datePreset(items: DatePreset[]) {
    this.currentDatePreset = [
      {
        id: '1',
        label: 'Last 7 days',
        startDate: moment().add(-7, 'days'),
        endDate: moment(),
      },
      {
        id: '2',
        label: 'Current Period',
        startDate: moment().add(-3, 'days'),
        endDate: moment().add(3, 'days'),
      },
    ];
    this.updateIdDatePreset();
  }
  @Input()
  set fixStartDate(date: string) {
    if (!!date && this.getMomentDate(date).isValid()) {
      this.fixedStartDate = this.getMomentDate(date);
      this.minimumDate = moment(this.fixedStartDate);
      this.selectedStartDate = moment(this.fixedStartDate);
    } else {
      this.fixedStartDate = null;
      this.minimumDate = null;
    }

    if (this.isInitialized) {
      this.makeDays();
    }
  }
  @Input()
  set fixEndDate(date: string) {
    if (!!date && this.getMomentDate(date).isValid()) {
      this.fixedEndDate = this.getMomentDate(date);
      this.maximumDate = moment(this.fixedEndDate);
      this.selectedEndDate = moment(this.fixedEndDate);
    } else {
      this.fixedEndDate = null;
      this.maximumDate = null;
    }

    if (this.isInitialized) {
      this.makeDays();
    }
  }
  @Input()
  set minDate(date: string) {
    if (!!date && this.getMomentDate(date).isValid() && !this.fixedStartDate) {
      this.minimumDate = this.getMomentDate(date);
    } else if (!this.fixedStartDate) {
      this.minimumDate = moment().add(-200, 'year');
    }

    if (this.isInitialized) {
      this.makeDays();
    }
  }
  @Input()
  set maxDate(date: string) {
    if (!!date && this.getMomentDate(date).isValid() && !this.fixedEndDate) {
      this.maximumDate = this.getMomentDate(date);
    } else if (!this.fixedEndDate) {
      this.maximumDate = moment().add(200, 'year');
    }

    if (this.isInitialized) {
      this.makeDays();
    }
  }
  @Input()
  set selectedDate(date: string) {
    if (!!date && this.getMomentDate(date).isValid()) {
      this.selectedDateObj.date = this.getMomentDate(date);
    } else {
      this.selectedDateObj.date = moment();
    }

    if (this.isInitialized) {
      this.makeDays();
    }
  }
  @Input()
  set startDate(date: string) {
    if (!!date && this.getMomentDate(date).isValid() && !this.fixedStartDate) {
      this.selectedStartDate = this.getMomentDate(date);
    } else {
      this.selectedStartDate = moment();
    }

    if (this.isInitialized) {
      this.makeDays();
    }
  }
  @Input()
  set endDate(date: string) {
    if (!!date && this.getMomentDate(date).isValid() && !this.fixedEndDate) {
      this.selectedEndDate = this.getMomentDate(date);
    } else {
      this.selectedEndDate = moment();
    }

    if (this.isInitialized) {
      this.makeDays();
    }
  }

  @Output()
  selectedDateChange = new EventEmitter<string>();
  @Output()
  selectedIntervalDateChange = new EventEmitter<DateIntervals>();

  @Output()
  startDateChange = new EventEmitter<string>();
  @Output()
  endDateChange = new EventEmitter<string>();

  language$: Subscription;

  isTodayEnabled = false;
  MonthItems: DateItem[];
  YearsItems: DateItem[];
  currentSelectedDatePreset: DatePreset;

  keyCustom = 'custom';
  keyToday = 'today';
  currentDatePreset: DatePreset[];
  minimumDate = moment().add(-200, 'year');
  maximumDate = moment().add(200, 'year');
  navDate: moment.Moment;
  weekDaysHeaderArr: string[] = [];
  days: DateItem[] = [];
  selectedDateObj = new DateItem(moment());

  selectedStartDate = moment();
  selectedStartDateObj = new DateItem();
  selectedEndDate = moment();
  selectedEndDateObj = new DateItem();

  fixedStartDate: moment.Moment;
  fixedEndDate: moment.Moment;

  localeStr = 'en';

  readonly datePanelSize = DatePanelSize;
  readonly datePanelView = DatePanelView;
  readonly datePanelMode = DatePanelMode;
  readonly datePresetServiceType = DatePresetServiceType;

  isInitialized = false;

  constructor(
    @Inject(LOCALE_ID) public localeId: string,
    private helperService: HelperService,
    private elementRef: ElementRef,
    private datePresetService: DatePresetService,
    private translateService: TranslateService,
  ) {
    super();
    this.elementRef = elementRef;
  }
  protected onInit(): void {
    this.isInitialized = true;
    this.language$ = this.translateService.language$.subscribe((language) => {
      this.localeStr = language;
      moment.locale(this.localeStr);
      this.updateCalendar();
    });
    this.updateSelectedDatePreset(this.selectedStartDate, this.selectedEndDate);
  }
  protected onDestroy(): void {
    if (this.language$) {
      this.language$.unsubscribe();
    }
  }

  private updateCalendar() {
    if (this.intervalSelection) {
      if (this.fixedStartDate) {
        this.selectedStartDate = moment(this.fixedStartDate);
      }

      if (this.fixedEndDate) {
        this.selectedEndDate = moment(this.fixedEndDate);
      }

      this.navDate = moment(this.selectedStartDate);
    } else {
      this.navDate = moment(this.selectedDateObj.date);
    }
    this.makeHeader();
    this.makeDays();

    this.updateMonths();
    this.updateYears(Number(this.getNavCurrentDate('YYYY')));

    this.isTodayEnabled = this.isSelectable(moment());
  }

  public changeNavMonth(num: number) {
    if (this.canChangeNavMonth(num)) {
      this.navDate.add(num, 'month');
      this.makeDays();
    }
  }

  public canChangeNavMonth(num: number) {
    const clonedDate = moment(this.navDate);
    clonedDate.add(num, 'month');

    return clonedDate.isBetween(this.minimumDate, this.maximumDate, 'month', '[]');
  }

  public getNavCurrentDate(format: string) {
    this.navDate.locale(this.localeStr);
    return this.navDate.format(format);
  }

  public selectDay(day: DateItem) {
    if (day.selectable) {
      if (this.intervalSelection) {
        this.selectIntervalDates(day);
        this.setBetweenDates();
      } else {
        if (this.selectedDateObj) {
          this.selectedDateObj.selected = false;
        }
        day.selected = true;
        this.selectedDateObj = day;
        this.emitChange();
      }
    }
  }

  private getMomentDate(date: string) {
    return moment(date, this.dateFormat);
  }

  private formatDate(date: moment.Moment) {
    return date.format(this.dateFormat);
  }

  private emitChange() {
    if (this.isInitialized) {
      if (this.intervalSelection) {
        this.updateSelectedDatePreset(this.selectedStartDate, this.selectedEndDate);
        const emittedValue = new DateIntervals(
          this.currentSelectedDatePreset.id,
          this.currentSelectedDatePreset.label,
          this.formatDate(this.selectedStartDate),
          this.formatDate(this.selectedEndDate),
        );
        this.selectedIntervalDateChange.emit(emittedValue);
        this.startDateChange.emit(emittedValue.startDate);
        this.endDateChange.emit(emittedValue.endDate);
      } else {
        this.selectedDateChange.emit(this.formatDate(this.selectedDateObj.date));
      }
    }
  }

  private makeHeader() {
    this.weekDaysHeaderArr = [];
    const weekDaysArr: number[] = [0, 1, 2, 3, 4, 5, 6];
    weekDaysArr.forEach((day) => this.weekDaysHeaderArr.push(moment().weekday(day).format('dd')));
  }

  private makeDays() {
    this.days = [];
    const arrayLength = 42;

    const firstDayDate = moment(this.navDate).startOf('month');
    const nbOfDaysFromPreviousMonth = firstDayDate.weekday();
    const previousMonth = moment(this.navDate).add(-1, 'month');
    for (let index = 0; index < nbOfDaysFromPreviousMonth; index++) {
      const date = moment(previousMonth).date(
        previousMonth.endOf('month').date() - nbOfDaysFromPreviousMonth + index + 1,
      );
      const obj = new DateItem(date, 'previous');
      this.days.push(obj);
    }

    const nbOfDaysInCurrentMonth = moment(this.navDate).endOf('month').date();
    for (let index = 0; index < nbOfDaysInCurrentMonth; index++) {
      const date = moment(this.navDate).date(index + 1);
      const obj = new DateItem(date, 'current');
      this.days.push(obj);
    }

    const nbOfDaysFromNextMonth = arrayLength - nbOfDaysFromPreviousMonth - nbOfDaysInCurrentMonth;
    const nextMonth = moment(this.navDate).add(1, 'month');
    for (let index = 0; index < nbOfDaysFromNextMonth; index++) {
      const date = moment(nextMonth).date(index + 1);
      const obj = new DateItem(date, 'next');
      this.days.push(obj);
    }

    for (let index = 0; index < arrayLength; index++) {
      const day = this.days[index];
      day.value = day.date.date();
      day.isToday = day.date.isSame(moment(), 'day');
      this.isSelected(day);
      this.isFixed(day);
      this.isFirstOrLastDayofWeek(day);
      this.setStartDateEqualsToEndDate(day);
      day.selectable = this.isSelectable(day.date);
      if (this.intervalSelection) {
        const dStart = this.selectedStartDate;
        const dEnd = this.selectedEndDate;
        day.isBetween = this.isBetween(day.date, dStart, dEnd);
      }
    }
  }

  isFirstOrLastDayofWeek(day: DateItem) {
    const cloneDate = moment(day.date);
    day.isFirstDayOfWeek =
      cloneDate.day() === 0 || cloneDate.isSame(moment(this.maximumDate).add(1, 'day'));
    day.isLastDayOfWeek =
      cloneDate.day() === 6 || cloneDate.isSame(moment(this.minimumDate).add(-1, 'day'));
  }

  private isSelected(day: DateItem) {
    if (this.intervalSelection) {
      const startDateSelected = day.date.isSame(this.selectedStartDate, 'day');

      const endDateSelected = day.date.isSame(this.selectedEndDate, 'day');

      if (startDateSelected) {
        this.selectedStartDate = day.date;
        this.selectedStartDateObj = day;
        this.selectedStartDateObj.isStartDate = startDateSelected && !endDateSelected;
        this.selectedStartDateObj.selected = true;
      }
      if (endDateSelected) {
        this.selectedEndDate = day.date;
        this.selectedEndDateObj = day;
        this.selectedEndDateObj.isEndDate = !startDateSelected && endDateSelected;
        this.selectedEndDateObj.selected = true;
      }
    } else {
      day.selected = this.selectedDateObj.date.isSame(day.date, 'day');
      if (day.selected) {
        this.selectedDateObj = day;
      }
    }
  }

  private isFixed(day: DateItem) {
    if (this.fixedStartDate) {
      day.isFixed = this.isSame(day.date, this.fixedStartDate);
    }

    if (this.fixedEndDate) {
      day.isFixed = day.isFixed || this.isSame(day.date, this.fixedEndDate);
    }
  }

  private isSelectable(date: moment.Moment): boolean {
    return date.isBetween(this.minimumDate, this.maximumDate, 'day', '[]');
  }

  private isBetween(date: moment.Moment, dateStart: moment.Moment, dateEnd: moment.Moment) {
    if (!date) {
      return false;
    }
    return date.isBetween(dateStart, dateEnd);
  }

  private setBetweenDates() {
    const dateStart = this.selectedStartDate;
    const dateEnd = this.selectedEndDate;

    this.days.forEach((day) => {
      day.isBetween = this.isBetween(day.date, dateStart, dateEnd);
    });
  }

  private setStartDateEqualsToEndDate(day: DateItem) {
    if (
      this.intervalSelection &&
      this.isSame(this.selectedStartDate, day.date) &&
      this.isSame(this.selectedStartDate, this.selectedEndDate) &&
      (!this.fixedStartDate || !this.fixedEndDate)
    ) {
      day.startEqualsEnd = true;
    } else {
      day.startEqualsEnd = false;
    }
  }

  private selectIntervalDates(day: DateItem) {
    this.selectedStartDateObj.startEqualsEnd = false;
    this.selectedEndDateObj.startEqualsEnd = false;

    this.selectedStartDateObj.selected = false;
    this.selectedEndDateObj.selected = false;

    const startDateCompare = this.compareDate(this.selectedStartDate, day.date);
    const endDateCompare = this.compareDate(this.selectedEndDate, day.date);
    const isStartAndEndEqual = this.compareDate(this.selectedStartDate, this.selectedEndDate) === 0;
    /*
      1) selected date is before start date and end date => set start date
      2) selected date is after start date and equals to end date => set start date

      3) selected date is equals to start date and before end date => set end date
      4) selected date is after start date and end date => set end date

      5) selected date is equals to start date and end date => do nothing
      6) selected date is before start date and equals to end date => impossible
      7) selected date is equals to start date and after end date => impossible
      8) selected date is before start date and after end date => impossible

      9) selected date is between start date and end date => select nearest date and if equals
      select start date
    */
    if (this.fixedStartDate || this.fixedEndDate) {
      this.selectNonFixedDate(day, isStartAndEndEqual);
    } else {
      this.selectStartAndEndDates(day, startDateCompare, endDateCompare, isStartAndEndEqual);
    }

    this.setStartDateEqualsToEndDate(day);
    this.emitChange();
  }

  private selectStartAndEndDates(
    day: DateItem,
    startDateCompare: number,
    endDateCompare: number,
    _isStartAndEndEqual: boolean,
  ) {
    day.selected = true;
    if (startDateCompare === -1 && endDateCompare === -1) {
      // this.selectStartDate(day);
      // this.selectEndDate(day);
      this.selectedStartDateObj.selected = false;
      this.selectedEndDateObj.selected = true;
      this.selectStartDate(day);
    } else if (startDateCompare === 1 && endDateCompare === 1) {
      this.selectedStartDateObj.selected = true;
      this.selectEndDate(day);
    } else if (startDateCompare === 0 || endDateCompare === 0) {
      this.selectStartDate(day);
      this.selectEndDate(day);
    } else if (startDateCompare === 1 && endDateCompare === -1) {
      // this.selectStartDate(day);
      // this.selectEndDate(day);
      this.selectedStartDateObj.selected = true;
      this.selectedEndDateObj.selected = false;
      this.selectEndDate(day);
    }
  }

  private selectNonFixedDate(day: DateItem, isStartAndEndEqual: boolean) {
    if (this.fixedStartDate && !this.fixedEndDate && !day.date.isBefore(this.fixedStartDate)) {
      if (!isStartAndEndEqual) {
        this.selectedEndDateObj.selected = false;
      }

      day.selected = true;
      this.selectEndDate(day);
    }

    if (this.fixedEndDate && !this.fixedStartDate && !day.date.isAfter(this.fixedEndDate)) {
      if (!isStartAndEndEqual) {
        this.selectedStartDateObj.selected = false;
      }

      day.selected = true;
      this.selectStartDate(day);
    }
  }

  private selectStartDate(day: DateItem) {
    this.selectedStartDateObj.isStartDate = false;
    this.selectedStartDateObj = day;
    this.selectedStartDate = moment(this.selectedStartDateObj.date);
    this.selectedStartDateObj.isStartDate = true;
    if (this.isSame(this.selectedStartDate, this.selectedEndDate)) {
      this.selectedStartDateObj.isStartDate = false;
      this.selectedEndDateObj.isEndDate = false;
    } else {
      this.selectedEndDateObj.isEndDate = true;
    }
  }

  private selectEndDate(day: DateItem) {
    this.selectedEndDateObj.isEndDate = false;
    this.selectedEndDateObj = day;
    this.selectedEndDate = moment(this.selectedEndDateObj.date);
    this.selectedEndDateObj.isEndDate = true;
    if (this.isSame(this.selectedEndDate, this.selectedStartDate)) {
      this.selectedStartDateObj.isStartDate = false;
      this.selectedEndDateObj.isEndDate = false;
    } else {
      this.selectedStartDateObj.isStartDate = true;
    }
  }

  private compareDate(d1: moment.Moment, d2: moment.Moment) {
    if (!d1 && !d2) {
      return 0;
    }

    if (!d2) {
      return -1;
    }

    if (d2.isAfter(d1)) {
      return 1;
    }
    if (d2.isBefore(d1)) {
      return -1;
    }
    if (this.isSame(d2, d1)) {
      return 0;
    }
  }

  private isSame(d1: moment.Moment, d2: moment.Moment): boolean {
    return this.formatDate(d1) === this.formatDate(d2);
  }

  private getDifference(d1: moment.Moment, d2: moment.Moment) {
    if (!d2) {
      return Number.MAX_SAFE_INTEGER;
    }

    return Math.abs(d2.diff(d1));
  }
  private updateMonths() {
    const items = Array<DateItem>();
    let index = 0;
    const clonedDate = moment(this.navDate);

    moment.months().forEach((month) => {
      clonedDate.month(month);

      const item = new DateItem();
      item.selectable = clonedDate.isBetween(this.minimumDate, this.maximumDate, 'month', '[]');
      item.selected = this.getNavCurrentDate('MMMM') === month;
      item.month = month;
      item.value = index++;
      items.push(item);
    });
    this.MonthItems = items;
  }
  private updateYears(beginDate: number) {
    const items = Array<DateItem>();
    const clonedDate = moment(this.navDate);
    for (let index = 0; index < 10; index++) {
      const year = Number(beginDate + index);
      const item = new DateItem();

      clonedDate.year(year);
      item.selectable = clonedDate.isBetween(this.minimumDate, this.maximumDate, 'year', '[]');

      item.selected = this.navDate.year() === year;
      item.value = year;
      items.push(item);
    }
    this.YearsItems = items;
  }

  public setMonth(monthNum: number) {
    this.navDate.month(monthNum);
    this.adjustDate(this.navDate);
    this.makeDays();
    this.view = this.datePanelView.Day;
  }

  public setYears(year: number) {
    this.navDate.year(year);
    this.adjustDate(this.navDate);
    this.makeDays();
    this.view = this.datePanelView.Day;
  }
  adjustDate(_date: moment.Moment) {
    if (this.navDate.isBefore(this.minimumDate)) {
      this.navDate = moment(this.minimumDate);
    }
    if (this.navDate.isAfter(this.maximumDate)) {
      this.navDate = moment(this.maximumDate);
    }
  }
  onChangeView(view: DatePanelView) {
    switch (view) {
      case DatePanelView.Month:
        this.updateMonths();
        break;
      case DatePanelView.Year:
        this.updateYears(Number(this.getNavCurrentDate('YYYY')));
        break;

      default:
        break;
    }

    this.view = view;
  }
  onSelectToday() {
    const item = new DateItem(moment());
    item.isToday = true;
    item.isStartDate = true;
    item.isEndDate = true;
    item.selectable = true;

    if (this.intervalSelection) {
      this.selectStartDate(item);
      this.selectEndDate(item);
    }

    this.selectDay(item);
    this.selectStartAndEndDates(item, 0, 0, true);
    this.updateCalendar();
  }
  public changeNavYear(num: number) {
    switch (num) {
      case -1:
        this.updateYears(this.YearsItems[0].value - 10);
        break;
      case 1:
        this.updateYears(this.YearsItems[this.YearsItems.length - 1].value + 1);
        break;
    }
  }

  public canChangeNavYear(num: number): boolean {
    switch (num) {
      case -1:
        return this.YearsItems[0].value - 1 >= this.minimumDate.year();

      case 1:
        return this.YearsItems[this.YearsItems.length - 1].value + 1 <= this.maximumDate.year();
    }
  }
  onSelectTodayYear() {
    this.onSelectToday();
    this.view = this.datePanelView.Day;
  }

  updateIdDatePreset() {
    this.currentDatePreset.forEach((item) => {
      if (this.helperService.isNullOrEmpty(item.id)) {
        item.id = this.helperService.newGuid();
      }
    });
  }
  updateSelectedDatePreset(startDate: moment.Moment, endDate: moment.Moment) {
    this.currentSelectedDatePreset = this.datePresetService.getDatePreset(
      this.currentDatePreset,
      startDate,
      endDate,
      this.dateFormat,
      this.mode === this.datePanelMode.Regular,
    );
  }

  onDatePresetSelectionChange(value: string) {
    this.updateCalendarFromPreset(value);
  }
  updateCalendarFromPreset(value: string) {
    const itemDatePreset = this.currentDatePreset.find((element) => element.id === value);
    if (itemDatePreset) {
      this.updateSelectedDatePreset(itemDatePreset.startDate, itemDatePreset.endDate);

      const itemStart = new DateItem(moment(itemDatePreset.startDate));
      itemStart.isStartDate = true;
      itemStart.isEndDate = true;
      itemStart.selectable = true;

      const itemEnd = new DateItem(moment(itemDatePreset.endDate));
      itemEnd.isStartDate = true;
      itemEnd.isEndDate = true;
      itemEnd.selectable = true;

      if (this.intervalSelection) {
        this.selectStartDate(itemStart);
        this.selectEndDate(itemEnd);
      }

      this.updateCalendar();
      this.emitChange();
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    // tslint:disable-next-line: forin
    for (const propName in changes) {
      switch (propName) {
        case 'datePreset':
        case 'startDate':
        case 'endDate':
          this.updateSelectedDatePreset(this.selectedStartDate, this.selectedEndDate);
          break;
        case 'minimumDate':
        case 'maximumDate':
        case 'selectedStartDate': {
          this.updateMonths();
          this.updateYears(this.navDate.year());
          break;
        }
      }
    }
  }
}
